import React, { useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { MapPin, Sparkle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function LocalPage({ embedded = false }) {
  const [service, setService] = useState("");
  const [location, setLocation] = useState("");
  const [offer, setOffer] = useState("");
  const [postResult, setPostResult] = useState(null);
  const [kwResult, setKwResult] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);
  const [loadingKw, setLoadingKw] = useState(false);

  const genPost = async () => {
    if (!service || !location) return toast.error("Service and location required");
    setLoadingPost(true);
    try { const r = await api.post("/local/post", { service, location, offer }); setPostResult(r.data); }
    catch { toast.error("Failed"); } finally { setLoadingPost(false); }
  };
  const genKw = async () => {
    if (!service || !location) return toast.error("Service and location required");
    setLoadingKw(true);
    try { const r = await api.post("/local/keywords", { topic: service, location }); setKwResult(r.data); }
    catch { toast.error("Failed"); } finally { setLoadingKw(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="local-page">
      {!embedded && <PageHeader eyebrow="Local SEO" title="Dominate searches in your service area." description="Google Business posts and local keyword research, tuned for property maintenance pros." />}
      <Card className="sharp-card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label className="text-xs uppercase tracking-wider font-semibold">Service</Label><Input data-testid="local-service" value={service} onChange={(e)=>setService(e.target.value)} placeholder="e.g. gutter cleaning" className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-semibold">Location</Label><Input data-testid="local-location" value={location} onChange={(e)=>setLocation(e.target.value)} placeholder="e.g. Denver, CO" className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-semibold">Offer (optional)</Label><Input data-testid="local-offer" value={offer} onChange={(e)=>setOffer(e.target.value)} placeholder="e.g. fall special" className="rounded-sm mt-1" /></div>
        </div>
      </Card>

      <Tabs defaultValue="posts" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-1 h-auto">
          <TabsTrigger value="posts" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Google Business Posts</TabsTrigger>
          <TabsTrigger value="kw" className="rounded-sm px-4 py-2 data-[state=active]:bg-slate-900 data-[state=active]:text-white">Local Keywords</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-6">
          <Button data-testid="btn-gen-local-post" onClick={genPost} disabled={loadingPost} className="bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11 mb-4">
            <Sparkle className="mr-2" size={18} />{loadingPost ? "Writing..." : "Generate 3 GBP posts"}
          </Button>
          {postResult?.variants && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {postResult.variants.map((v, i) => (
                <Card key={v.text || i} className="sharp-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="rounded-sm bg-slate-900 text-white">Variant {i+1}</Badge>
                    <Button variant="outline" size="sm" onClick={()=>copy(v.text)} className="rounded-sm"><Copy size={12} className="mr-1" />Copy</Button>
                  </div>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{v.text}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-navy"><MapPin size={12} />CTA: {v.cta_button}</div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kw" className="mt-6">
          <Button data-testid="btn-gen-local-kw" onClick={genKw} disabled={loadingKw} className="bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11 mb-4">
            <Sparkle className="mr-2" size={18} />{loadingKw ? "Researching..." : "Find local keywords"}
          </Button>
          {kwResult?.keywords && (
            <Card className="sharp-card p-6">
              <div className="space-y-2">
                {kwResult.keywords.map((k) => (
                  <div key={k.keyword} className="flex items-start gap-3 border-b border-slate-100 pb-2">
                    <Badge className="rounded-sm bg-navy text-white shrink-0">{k.intent}</Badge>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{k.keyword}</div>
                      <div className="text-xs text-slate-500">{k.why}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
